import { useEffect, useMemo, useState } from 'react';
import {
    useGetRolesQuery,
    useLazyGetPermissionsByRoleIdQuery,
    useLazyGetUserByIdQuery
} from '../../../../../services/adminApi';
import { useGetAllDocumentTypesQuery } from '../../../../../services/documentsApi';
import { handleErrors } from '../../../../../utils/functions';
import { capitalize } from 'lodash';

function useUserShow({ drawerAction, selectedItem }: any) {
    const [data, setData] = useState<any>(null);
    const [loadingUserData, setLoadingUserData] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [getUserById] = useLazyGetUserByIdQuery();

    const [
        getPermissionsByRoleId,
        { isLoading: loadingRolePermissions, isFetching: fetchingRolePermissions }
    ] = useLazyGetPermissionsByRoleIdQuery();

    const {
        data: roles,
        isLoading: loadingRoles,
        isFetching: fetchingRoles
    } = useGetRolesQuery(
        {},
        {
            refetchOnMountOrArgChange: false,
            skip: drawerAction !== 'view'
        }
    );

    const {
        data: allDocumentTypes,
        isLoading: loadingAllDocumentTypes,
        isFetching: fetchingAllDocumentTypes
    } = useGetAllDocumentTypesQuery(
        {},
        { refetchOnMountOrArgChange: false, skip: drawerAction !== 'view' }
    );

    const handleChangeTabValue = (event: React.SyntheticEvent | null, newValue: number) => {
        setTabValue(newValue);
    };

    const getFullName = (selectedItem: any) => {
        const auxFullName = selectedItem?.nombre + ' ' + selectedItem?.apellido;
        return auxFullName
            .split(' ')
            .map((word: string) => capitalize(word))
            .join(' ');
    };

    useEffect(() => {
        if (selectedItem?.id && drawerAction === 'view' && !!allDocumentTypes?.length) loadData();
        // eslint-disable-next-line
    }, [selectedItem, drawerAction, allDocumentTypes]);

    const getRolePermissions = async (id: number) => {
        const response: any = await getPermissionsByRoleId(id);
        if (!response?.error) {
            return response?.data?.permisos;
        } else {
            handleErrors(response?.error);
        }
    };

    const loadData = async () => {
        try {
            setLoadingUserData(true);
            const response: any = await getUserById(selectedItem?.id).unwrap();
            if (!response?.error) {
                const rolePermissions = await getRolePermissions(response?.rol?.id);

                setData({
                    apellido: response?.apellido,
                    cuil: response?.cuil,
                    nombre: response?.nombre,
                    esAdmin: response?.esAdmin,
                    id: response?.id,
                    idRol: { ...response?.rol, permisos: getPermissions(rolePermissions) },
                    permisos: getPermissions(response?.permisos)
                });
                if (tabValue !== 0) setTabValue(0);
            } else {
                handleErrors(response?.error);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingUserData(false);
        }
    };

    const getPermissions = (permissions: any) => {
        return permissions?.map((permission: any) => ({
            id: Number(permission.tipoDocumento.id),
            cargar: permission.cargar,
            descargar: permission.descargar,
            eliminar: permission.eliminar,
            tipoDocumento: allDocumentTypes?.find(
                (ed: any) => ed.tipoDocumento.id === Number(permission.tipoDocumento.id)
            )?.tipoDocumento
        }));
    };

    const formattedRoles = useMemo(() => {
        if (!roles || !allDocumentTypes) return [];
        return roles?.map((role: any) => ({
            ...role,
            permisos: getPermissions(role?.permisos)
        }));
        // eslint-disable-next-line
    }, [roles, allDocumentTypes]);

    const clearData = () => setData(null);

    const loading = useMemo(
        () => loadingAllDocumentTypes || fetchingAllDocumentTypes || loadingRoles || fetchingRoles,
        [loadingAllDocumentTypes, fetchingAllDocumentTypes, loadingRoles, fetchingRoles]
    );

    return {
        formattedRoles,
        loading,
        loadingUserData,
        data,
        allDocumentTypes,
        tabValue,
        handleChangeTabValue,
        clearData,
        getFullName,
        loadingRolePermissions,
        fetchingRolePermissions
    };
}

export default useUserShow;
